// src/hooks/nativeHlsSeekDiag.ts
//
// Black-box recorder for the iOS native-HLS path (`video.src = signed m3u8`).
//
// Why: on that path there is no hls.js, so a seek that misbehaves inside
// AVPlayer leaves nothing in our logs — the backend just sees 200s, and the
// phone shows a frozen frame (or, as observed 2026-09-07, the duration
// collapsing to the seek target and the item "ending" while AVPlayer raced
// through every remaining segment). We can't drive AVPlayer from CI, so this
// is the one window we get: keep a rolling timeline of media events with the
// element state at each, and report it (Sentry warning, readable via the API)
// whenever something looks wrong:
//
//   - `error`                        → "media error"
//   - `durationchange` shrinking >10% → "duration shrank"
//   - `ended` before 95% of the longest duration seen → "ended early"
//   - a seek not settled after `stallMs`  → "seek never completed" / "post-seek stall"
//   - detach while a seek is pending  → "seek pending at teardown"
//
// A seek is "settled" once `seeked` has fired AND the element is paused,
// `playing` fired, or the playhead advanced past the seek target.

import { Sentry } from '@/observability/sentry';

export const NATIVE_SEEK_STALL_MS = 8_000;
const MAX_TIMELINE = 80;
const MAX_REPORTS = 4;

export interface NativeSeekReport {
  message: string;
  context: Record<string, unknown>;
}

export interface NativeSeekDiagOptions {
  stallMs?: number;
  /** Injectable sink (defaults to Sentry + console.warn). */
  report?: (r: NativeSeekReport) => void;
  now?: () => number;
}

/** [event, ms since attach, currentTime, duration, readyState] */
export type TimelineEntry = [string, number, number, number, number];

function round(n: number): number { return Math.round(n * 100) / 100; }

function ranges(r: TimeRanges): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let i = 0; i < r.length; i += 1) out.push([round(r.start(i)), round(r.end(i))]);
  return out;
}

export function snapshotVideo(video: HTMLVideoElement): Record<string, unknown> {
  let srcHost = '';
  try { srcHost = new URL(video.currentSrc || video.src).host; } catch { /* no src */ }
  return {
    currentTime: round(video.currentTime),
    duration: round(video.duration),
    paused: video.paused,
    ended: video.ended,
    readyState: video.readyState,
    networkState: video.networkState,
    buffered: ranges(video.buffered),
    seekable: ranges(video.seekable),
    error: video.error ? { code: video.error.code, message: video.error.message } : null,
    srcHost,
    userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
  };
}

function defaultReport(r: NativeSeekReport): void {
  // eslint-disable-next-line no-console
  console.warn(`[native-hls] ${r.message}`, r.context);
  try {
    Sentry.captureMessage(`native-hls: ${r.message}`, { level: 'warning', extra: r.context });
  } catch { /* observability must never break playback */ }
}

const OBSERVED_EVENTS = [
  'loadedmetadata', 'durationchange', 'seeking', 'seeked', 'playing', 'pause', 'play',
  'waiting', 'stalled', 'canplay', 'ended', 'emptied', 'error', 'timeupdate',
] as const;

/**
 * Attach the recorder to a natively-playing <video>. Returns a detach fn.
 */
export function attachNativeSeekDiag(
  video: HTMLVideoElement,
  opts: NativeSeekDiagOptions = {},
): () => void {
  const stallMs = opts.stallMs ?? NATIVE_SEEK_STALL_MS;
  const report = opts.report ?? defaultReport;
  const now = opts.now ?? ((): number => Date.now());
  const attachedAt = now();

  const timeline: TimelineEntry[] = [];
  let maxDuration = 0;
  let reports = 0;

  let seekStartedAt: number | null = null;
  let seekTarget = 0;
  let seekedSeen = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  function record(name: string): void {
    const d = Number.isFinite(video.duration) ? video.duration : 0;
    timeline.push([name, now() - attachedAt, round(video.currentTime), round(d), video.readyState]);
    if (timeline.length > MAX_TIMELINE) timeline.shift();
  }
  function emit(message: string, extra: Record<string, unknown> = {}): void {
    if (reports >= MAX_REPORTS) return;
    reports += 1;
    report({
      message,
      context: {
        ...extra,
        maxDuration: round(maxDuration),
        seekTarget: seekStartedAt == null ? null : round(seekTarget),
        timeline: [...timeline],
        ...snapshotVideo(video),
      },
    });
  }
  function clearSeek(): void {
    if (timer) { clearTimeout(timer); timer = null; }
    seekStartedAt = null;
    seekedSeen = false;
  }
  function settleIf(cond: boolean): void {
    if (seekStartedAt == null || !seekedSeen || !cond) return;
    clearSeek();
  }

  const handlers: Record<string, () => void> = {
    seeking: () => {
      clearSeek();
      seekStartedAt = now();
      seekTarget = video.currentTime;
      timer = setTimeout(() => {
        if (seekStartedAt == null) return;
        emit(seekedSeen ? 'post-seek stall' : 'seek never completed', { stallMs });
        clearSeek();
      }, stallMs);
    },
    seeked: () => { seekedSeen = true; settleIf(video.paused); },
    playing: () => settleIf(true),
    pause: () => settleIf(true),
    timeupdate: () => settleIf(video.currentTime > seekTarget + 0.25),
    durationchange: () => {
      const d = Number.isFinite(video.duration) ? video.duration : 0;
      if (maxDuration > 0 && d > 0 && d < maxDuration * 0.9) {
        emit('duration shrank', { from: round(maxDuration), to: round(d) });
      }
      maxDuration = Math.max(maxDuration, d);
    },
    ended: () => {
      if (maxDuration > 0 && video.currentTime < maxDuration * 0.95) emit('ended early');
    },
    error: () => { emit('media error'); clearSeek(); },
  };

  const listeners: Array<[string, () => void]> = OBSERVED_EVENTS.map((ev) => {
    // `timeupdate` fires several times a second; keep it out of the timeline
    // (the other entries carry currentTime) but still use it to settle seeks.
    const fn = (): void => {
      if (ev !== 'timeupdate') record(ev);
      handlers[ev]?.();
    };
    return [ev, fn];
  });
  for (const [ev, fn] of listeners) video.addEventListener(ev, fn);

  return (): void => {
    if (seekStartedAt != null) emit('seek pending at teardown');
    for (const [ev, fn] of listeners) video.removeEventListener(ev, fn);
    clearSeek();
  };
}
